// osc-telemetry/frontend/src/pages/DesktopLogin.tsx

import { useState, useEffect, useRef } from "react";

export function DesktopLogin() {
  const [status, setStatus] = useState<"idle" | "waiting" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const tokenRef = useRef<string | null>(null);

  const handleSignIn = async () => {
    setStatus("waiting");
    setErrorMsg(null);

    try {
      // Step 1: Initiate desktop auth — get a one-time token
      const res = await fetch("/api/ss/auth/desktop/initiate", { method: "POST" });
      if (!res.ok) throw new Error("Failed to initiate auth");
      const { desktop_token, login_url } = await res.json();
      tokenRef.current = desktop_token;

      // Step 2: Open login page in system browser
      try {
        const { open } = await import("@tauri-apps/plugin-shell");
        await open(login_url);
      } catch {
        window.open(login_url, "_blank");
      }

      // Step 3: Poll for completion
      pollRef.current = setInterval(async () => {
        try {
          const pollRes = await fetch(`/api/ss/auth/desktop/poll?token=${desktop_token}`);
          const pollData = await pollRes.json();

          if (pollData.status === "complete") {
            if (pollRef.current) clearInterval(pollRef.current);
            // Step 4: Claim — navigate WebView to claim endpoint (sets cookies via redirect)
            window.location.href = `/api/ss/auth/desktop/claim?token=${desktop_token}`;
          } else if (pollData.status === "expired") {
            if (pollRef.current) clearInterval(pollRef.current);
            setStatus("error");
            setErrorMsg("Session expired. Please try again.");
          }
        } catch {
          // Network error — keep polling
        }
      }, 2000);
    } catch (err: any) {
      setStatus("error");
      setErrorMsg(err.message || "Failed to start sign in");
    }
  };

  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        width: "100%",
        background: "var(--bg1)",
      }}
    >
      {/* Desktop drag region — allows window dragging from the top strip */}
      <div
        data-tauri-drag-region=""
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          height: "48px",
          zIndex: 50,
          // @ts-ignore
          WebkitAppRegion: "drag",
        }}
      />
      <div
        style={{
          background: "var(--card)",
          border: "1px solid var(--border)",
          borderRadius: "12px",
          padding: "40px",
          maxWidth: "440px",
          width: "100%",
          boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
          textAlign: "center",
        }}
      >
        <h1
          style={{
            fontSize: "32px",
            fontWeight: 800,
            color: "var(--text1)",
            marginBottom: "8px",
            letterSpacing: "-0.025em",
          }}
        >
          SimSense
        </h1>
        <p style={{ color: "var(--text2)", fontSize: "15px", marginBottom: "32px" }}>
          Sign in via your web browser to continue.
        </p>

        <button
          onClick={handleSignIn}
          disabled={status === "waiting"}
          style={{
            width: "100%",
            padding: "14px",
            background: status === "waiting" ? "var(--accent-dim, rgba(59,130,246,0.4))" : "var(--accent)",
            color: "#fff",
            border: "none",
            borderRadius: "8px",
            fontSize: "16px",
            fontWeight: 700,
            cursor: status === "waiting" ? "default" : "pointer",
            marginTop: "8px",
            transition: "all 0.15s ease",
            boxShadow: status === "waiting" ? "none" : "0 4px 12px rgba(59, 130, 246, 0.3)",
            opacity: status === "waiting" ? 0.8 : 1,
          }}
        >
          {status === "waiting" ? "Waiting for sign in..." : status === "error" ? "Try Again" : "Sign In"}
        </button>
        {status === "waiting" && (
          <p style={{ color: "var(--text3)", fontSize: "13px", marginTop: "16px" }}>
            Complete sign in on your browser. This page will update automatically.
          </p>
        )}
        {status === "error" && errorMsg && (
          <p style={{ color: "#f87171", fontSize: "13px", marginTop: "16px" }}>
            {errorMsg}
          </p>
        )}
      </div>
    </div>
  );
}
